import React from "react";
import { FileText, Download, Trash2, Loader2, Search, CheckCircle, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { RequirementDocument } from "@/types/document";

interface DocumentItemProps {
  document: RequirementDocument;
  isDeleting: string | null;
  expandedDocId: string | null;
  onExpand: (id: string | null) => void;
  onDelete: (doc: RequirementDocument) => void;
  onDownload: (doc: RequirementDocument) => void;
}

export function DocumentItem({
  document,
  isDeleting,
  expandedDocId,
  onExpand,
  onDelete,
  onDownload
}: DocumentItemProps) {
  const getDocumentTypeName = (type: string) => {
    const types: Record<string, string> = {
      "compliance_evidence": "Compliance Evidence",
      "certification": "Certification",
      "test_results": "Test Results",
      "declaration": "Declaration",
      "other": "Other"
    };
    return types[type] || type;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'complete':
        return (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 gap-1">
            <CheckCircle className="h-3 w-3" />
            Complete
          </Badge>
        );
      case 'in_progress':
        return (
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 gap-1">
            <Loader2 className="h-3 w-3 animate-spin" />
            In Progress
          </Badge>
        );
      case 'queued':
      default:
        return (
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 gap-1">
            <Clock className="h-3 w-3" />
            Queued
          </Badge>
        );
    }
  };

  const getRelevanceScore = (score: number | null) => {
    if (score === null) return null;
    
    return (
      <div className="space-y-1">
        <div className="flex justify-between items-center text-xs">
          <span className="text-muted-foreground">Relevance Score</span>
          <span className="font-medium">{score}%</span>
        </div>
        <Progress value={score} className="h-1" />
      </div>
    );
  };

  return (
    <div className="border rounded-md bg-card">
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center space-x-3">
          <FileText className="h-6 w-6 text-primary" />
          <div>
            <p className="font-medium">{document.file_name}</p>
            <div className="flex flex-wrap gap-2 mt-1">
              <span className="text-xs text-muted-foreground">
                {getDocumentTypeName(document.document_type)} • {new Date(document.uploaded_on).toLocaleDateString()}
              </span>
              {getStatusBadge(document.status)}
            </div>
          </div>
        </div>
        <div className="flex space-x-2">
          <Button 
            variant="outline" 
            size="icon" 
            onClick={() => onExpand(expandedDocId === document.id ? null : document.id)}
            title="View details"
          >
            <Search className="h-4 w-4" />
          </Button>
          <Button 
            variant="outline" 
            size="icon" 
            onClick={() => onDownload(document)}
            title="Download document"
          >
            <Download className="h-4 w-4" />
          </Button>
          <Button 
            variant="outline" 
            size="icon" 
            onClick={() => onDelete(document)}
            disabled={isDeleting === document.id}
            title="Delete document"
          >
            {isDeleting === document.id ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4 text-destructive" />
            )}
          </Button>
        </div>
      </div>
      
      {expandedDocId === document.id && (
        <div className="p-3 border-t bg-muted/30">
          {getRelevanceScore(document.relevance_score)}
          
          {document.assessment && (
            <div className="mt-3">
              <h4 className="text-sm font-medium mb-1">Assessment</h4>
              <p className="text-sm text-muted-foreground">{document.assessment}</p>
            </div>
          )}
          
          <div className="mt-3 text-xs text-muted-foreground">
            <p>Document uploaded on {new Date(document.uploaded_on).toLocaleString()}</p>
          </div>
        </div>
      )}
    </div>
  );
}
